import AppContext from "@/context/AppContext";
import { deleteEvent, updateEvent } from "@/lib/api";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import CloseIcon from '@mui/icons-material/Close';
import LinkIcon from "@mui/icons-material/Link";
import {Link} from "@mui/material";
import Button from "@mui/material/Button";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import {useContext, useState} from "react";
import {FormattedDate, FormattedTime} from "react-intl";
import theme from "../styles/theme";
import {useConfirm} from "./material-ui-confirm";
import {Box} from "@mui/system";
import Fade from "@mui/material/Fade";
import EventForm from "@/components/event/EventForm";
import { basierCirleBold, basierCirleRegular } from "@/styles/fonts";

export const EventRow = ({
                             bandProfile,
                             event,
                             editEvents = false,
                             onChanged,
                             ref,
                             ...props
                         }) => {
    const [eventFormOpen, setEventFormOpen] = useState(false);
    const {setSnackbar} = useContext(AppContext);
    const confirm = useConfirm();

    const handleDelete = () => {
        confirm({
            title: "Event löschen",
            description: `Möchtest Du das Event wirklich löschen?`,
            confirmationText: `Ich bin mir sicher. Event löschen.`,
        })
            .then(async () => {
                const old = await deleteEvent(event.id);
                setSnackbar("Und weg! Du hast das Event gelöscht!");
                if (onChanged) {
                  onChanged(old);
                }
            })
            .catch((error) => {
            });
    };

    const handleEventUpdated = async(formValues) => {
      const bandIds = formValues.bands.map((band => {
        return band.id;
      }));

      const dataToUpdate = {
        ...formValues,
        bands: bandIds,
        zipcode: formValues.zipcode.id,
        location: formValues.location?.id ?? null
      }

      const ev = await updateEvent(event.id, dataToUpdate);
      if (onChanged && typeof onChanged === "function") {
        onChanged(ev);
      }

      setSnackbar("Das Event wurde erfolgreich bearbeitet!");
      setEventFormOpen(false);
    }

    return (
        <>
            <Grid
                container
                alignItems="center"
                sx={{
                    lineHeight: "1rem",
                    [theme.breakpoints.up("md")]: {
                        lineHeight: "inherit",
                    },
                    "& .MuiTypography-root": {
                        textTransform: "uppercase",
                        fontSize: "0.75rem",
                        lineHeight: "0.57rem",
                        [theme.breakpoints.up("md")]: {
                            fontSize: "1.125rem",
                            lineHeight: "1.125rem",
                        },
                    },
                }}
            >
                <Grid item xs={7} sm>
                    <Typography variant="body1" component="span">
                        <FormattedDate value={event.date_start}/>
                    </Typography>
                    {(event.time_start && new Date(event.time_start).getTime() > 0) && (
                      <Typography variant="body1" component="span">
                        ,{" "}
                        <FormattedTime
                            value={(new Date(event.time_start).getTime() + (new Date(event.time_start).getTimezoneOffset()*60000))}
                            hour="numeric"
                            minute="numeric"
                        />
                      </Typography>
                    )}
                    {event.date_end && (
                        <Typography variant="body1" component="span">
                            {" - "}
                            <FormattedDate value={event.date_end}/>
                        </Typography>
                    )}
                </Grid>
                <Grid item xs sx={{display: {xs: "none", md: "block"}}}>
                    <Typography
                        variant="body1"
                        sx={{
                            "&.MuiTypography-root": {
                                fontFamily: basierCirleBold.style.fontFamily,
                                fontWeight: basierCirleBold.style.fontWeight,
                            },
                        }}
                    >
                        {event.name}
                    </Typography>
                </Grid>
                <Grid item xs={3} sm>
                    <Typography
                        variant="body1"
                        sx={{
                            "&.MuiTypography-root": {
                                fontFamily: basierCirleBold.style.fontFamily,
                                fontWeight: basierCirleBold.style.fontWeight,
                            },
                        }}
                    >
                        {event.location?.zipcode?.label || event.zipcode?.label}
                    </Typography>
                </Grid>
                <Grid item sm sx={{display: {xs: "none", sm: "block"}}}>
                    <Typography variant="body1">{event.location?.name}</Typography>
                </Grid>
                <Grid item xs={2} sm sx={{textAlign: "right",}}>
                    {event.link && (
                        <Link
                            href={event.link}
                            target="_blank"
                            rel="noopener noreferrer"
                            sx={{
                                [theme.breakpoints.only("xs")]: {
                                    display: editEvents && 'none',
                                },
                            }}
                        >
                            <Button
                                disableRipple
                                color="secondary"
                                startIcon={<LinkIcon/>}
                                sx={{
                                    "&.MuiButton-root": {
                                        padding: "0",
                                        fontSize: ".75rem",
                                        [theme.breakpoints.up("md")]: {
                                            fontSize: "1.125rem",
                                        },
                                        fontWeight: basierCirleRegular.style.fontWeight,
                                        fontFamily: basierCirleRegular.style.fontFamily,
                                        "&:hover": {
                                            background: "transparent",
                                            opacity: ".75",
                                        },
                                    },
                                }}
                            >
                                <Box sx={{display: {xs: "none", md: "inline-flex"},}}>Weitere Infos</Box>
                            </Button>
                        </Link>
                    )}
                </Grid>
                {editEvents && (
                    <Grid item xs={12} sm>
                        <Grid container spacing={0.5} justifyContent="flex-end">
                            <Grid item xs="auto">
                                <Button
                                    size="small"
                                    variant="outlinedSizeSmall"
                                    color="primary"
                                    aria-label="Bearbeiten"
                                    startIcon={!eventFormOpen ? <EditIcon/> : <CloseIcon/>}
                                    onClick={() => setEventFormOpen(!eventFormOpen)}
                                />
                            </Grid>
                            <Grid item xs="auto">
                                <Button
                                    size="small"
                                    variant="outlinedSizeSmall"
                                    color="primary"
                                    aria-label="Löschen"
                                    startIcon={<DeleteIcon/>}
                                    onClick={handleDelete}
                                />
                            </Grid>
                        </Grid>
                    </Grid>
                )}
            </Grid>
            {eventFormOpen &&
                <Fade in={eventFormOpen}>
                    <Box ref={ref} {...props}>
                        <EventForm
                            event={event}
                            bandProfile={bandProfile}
                            onChange={handleEventUpdated}
                        />
                    </Box>
                </Fade>
            }
        </>
    );
};
